import {
  collection,
  doc,
  FieldPath,
  getDoc,
  getDocs,
  query,
  runTransaction,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  buildFoodOrderRows,
  makeFoodOrderCostRecord,
  parseFoodCost,
} from "../utils/foodReport";

export async function fetchFoodReportTransactions(
  period,
  selectedMonth,
  now = new Date(),
) {
  const transactions = collection(db, "transactions");
  let reportQuery = transactions;
  if (period === "thisYear") {
    reportQuery = query(transactions, where("Year", "==", now.getFullYear()));
  } else if (period !== "allTime") {
    const [year, month] =
      period === "month"
        ? selectedMonth.split("-").map(Number)
        : [now.getFullYear(), now.getMonth() + 1];
    if (!year || !month || month > 12) throw new Error("Choose a valid month.");
    reportQuery = query(
      transactions,
      where("Month", "==", month),
      where("Year", "==", year),
    );
  }
  const snapshot = await getDocs(reportQuery);
  return snapshot.docs.map((item) => ({ ...item.data(), id: item.id }));
}

export async function fetchFoodReportPreferences() {
  const [preferences, menu] = await Promise.all([
    getDoc(doc(db, "reportSettings", "foodProfit")),
    getDocs(collection(db, "foodItems")),
  ]);
  return {
    includedFoods:
      preferences.exists() && Array.isArray(preferences.data().includedFoods)
        ? preferences.data().includedFoods
        : null,
    foodItems: menu.docs.map((item) => ({ ...item.data(), id: item.id })),
  };
}

export async function saveIncludedFoods(includedFoods) {
  await setDoc(
    doc(db, "reportSettings", "foodProfit"),
    {
      includedFoods: Array.from(new Set(includedFoods)),
    },
    { merge: true },
  );
}

export async function saveFoodOrderCost(row, value, includedFoods) {
  const cost = parseFoodCost(value);
  const reference = doc(db, "transactions", row.transactionId);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(reference);
    if (!snapshot.exists())
      throw new Error("This order was deleted. Refresh the report.");
    const data = snapshot.data();
    const latest = buildFoodOrderRows(
      [{ ...data, id: snapshot.id }],
      includedFoods,
    ).find((order) => order.orderId === row.orderId);
    if (
      !latest ||
      latest.itemsSignature !== row.itemsSignature ||
      latest.cost !== row.cost
    ) {
      throw new Error(
        "This order changed since you opened it. Refresh the report before saving.",
      );
    }
    const record = makeFoodOrderCostRecord(latest, cost);
    // Keep supplier costs separate from the sales array and all customer payment fields.
    transaction.update(
      reference,
      new FieldPath("FoodOrderProfitCosts", row.orderId),
      record,
    );
    return {
      ...data,
      id: snapshot.id,
      FoodOrderProfitCosts: {
        ...data.FoodOrderProfitCosts,
        [row.orderId]: record,
      },
    };
  });
}
